import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGuidedChat,
  useUpdateGuidedChat,
  type GuidedChatUpdateFeedbackRating,
} from "../hooks/useGuidedChat";

type FeedbackRating = NonNullable<GuidedChatUpdateFeedbackRating>;

export function CompletionFlowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const chatId = id ? parseInt(id, 10) : null;

  const { data: chat, isLoading: chatLoading } = useGuidedChat(chatId);
  const updateChat = useUpdateGuidedChat();
  const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(
    null
  );
  const handleContinue = useCallback(() => {
    if (!chatId) return;
    if (selectedRating) {
      updateChat.mutate(
        { guidedChatId: chatId, feedbackRating: selectedRating },
        { onSuccess: () => navigate("/") }
      );
    } else {
      navigate("/");
    }
  }, [chatId, navigate, selectedRating, updateChat]);

  if (chatLoading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-[#F8F8F6]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  if (!chat) {
    navigate("/");
    return null;
  }

  return (
    <FeedbackScreen
      selectedRating={selectedRating}
      onSelectRating={setSelectedRating}
      onContinue={handleContinue}
      isSubmitting={updateChat.isPending}
    />
  );
}

// Sub-components

interface FeedbackScreenProps {
  selectedRating: FeedbackRating | null;
  onSelectRating: (rating: FeedbackRating) => void;
  onContinue: () => void;
  isSubmitting: boolean;
}

function FeedbackScreen({
  selectedRating,
  onSelectRating,
  onContinue,
  isSubmitting,
}: FeedbackScreenProps) {
  const ratings: { value: FeedbackRating; label: string; icon: string }[] = [
    { value: "no", label: "No", icon: "👎" },
    { value: "a_little", label: "A little", icon: "👍" },
    { value: "yes", label: "Yes", icon: "❤️" },
  ];

  return (
    <div className="min-h-screen-safe flex flex-col bg-[#F8F8F6]">
      <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-md mx-auto w-full">
        <h1 className="text-3xl font-serif text-center text-text-primary mb-4">
          You've completed today's reflection!
        </h1>
        <p className="text-center text-text-secondary">
          Reflecting on your relationship takes courage. You've taken an
          important first step.
        </p>
      </div>

      <div className="px-6 pb-6 max-w-md mx-auto w-full">
        <p className="text-center text-text-secondary mb-4">
          Did this reflection feel helpful today?
        </p>

        <div className="flex gap-4 justify-center mb-6">
          {ratings.map((rating) => (
            <button
              key={rating.value}
              onClick={() => onSelectRating(rating.value)}
              className={`flex flex-col items-center justify-center w-24 h-24 rounded-xl border-2 transition-colors ${
                selectedRating === rating.value
                  ? "border-brand-primary bg-brand-primary/5"
                  : "border-gray-200 bg-white"
              }`}
            >
              <span className="text-2xl mb-1">{rating.icon}</span>
              <span className="text-sm text-text-primary">{rating.label}</span>
            </button>
          ))}
        </div>

        <button
          onClick={onContinue}
          disabled={isSubmitting}
          className="w-full bg-brand-primary text-white py-4 rounded-full font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Saving..." : "Continue"}
        </button>
      </div>
    </div>
  );
}
